<template>
  <v-container class="fill-height light" fluid light>
    <v-row class="wrap d-flex justify-center align-content-center">
      <v-col lg="8" sm="12" md="10">
        <h1 class="deep-orange--text darken-3 mt-2 mb-3">Créer un projet d'aide et trouver des bénévoles</h1>
        <v-form
          ref="addNewProject"
          v-model="valid"
          :validation="true"
          class="elevation-1 pa-3"
        >
          <v-text-field
            class="mb-4"
            v-model="title"
            :counter="50"
            label="Titre"
            color="deep-orange darken-3"
            type="text"
            required
            hint="Le titre doit être court et doit permettre de comprendre rapidement le besoin d'aide"
            :persistent-hint="true"
          ></v-text-field>

          <v-textarea
            v-model="presentation"
            placeholder="Décrivez précisément le besoin, le déroulement de la mission (lieu, horaire, etc), pour que le futur bénévole se projette dans sa mission d'aide"
            class="mb-4"
            prepend-inner-icon="mdi-comment"
            label="Présentation du besoin"
            rows="4"
            :auto-grow="true"
            :counter="250"
            clearable
            hint="Présentez le besoin d'aide afin que le futur bénévole se projette"
            :persistent-hint="true"
            required
            color="deep-orange darken-3"
          ></v-textarea>

          <v-select
            class="mb-4"
            v-model="need"
            :items="needs"
            item-value="need"
            item-text="examples" 
            :two-line="true"
            :rules="[v => !!v || 'Le type de besoin est requis']"
            label="Type de besoin"
            hint="Choisissez bien le type de besoin pour la classification des besoins sur l'accueil"
            :persistent-hint="true"
            append-icon="mdi-plus"
            required
            color="deep-orange darken-3"
          >
            <template slot="selection" slot-scope="data">
              <!-- HTML that describe how select should render selected items -->
              {{ data.item.need }}
            </template>
            <template slot="item" slot-scope="data">
              <!-- HTML that describe how select should render items when the select is open -->
              <v-list>
                <v-list-item-title class="deep-orange--text darken-3" v-html="data.item.need"></v-list-item-title>
                <v-list-item-content v-html="data.item.examples"></v-list-item-content>
              <v-divider></v-divider>
              </v-list>
            </template>
          </v-select>

          <v-text-field
            class="mb-4"
            v-model="email"
            :rules="emailRules"
            label="Email"
            hint="Email de la personne que le bénévole doit contacter"
            :persistent-hint="true"
            color="deep-orange darken-3"
          ></v-text-field>

          <v-text-field
            class="mb-4"
            v-model="phone"
            :rules="phoneRules"
            label="Téléphone"
            type="tel"
            hint="Téléphone de la personne que le bénévole doit contacter"
            :persistent-hint="true"
            color="deep-orange darken-3"
          ></v-text-field>

          <v-checkbox
            class="mb-4"
            v-model="rgpd"
            :rules="[v => !!v || 'Vous devez être d\'accord pour continuer']"
            label="J'accepte que ces données soit stockées pour le bon fonctionnement du site "
            required
            color="deep-orange darken-3"
          ></v-checkbox>
          <v-alert
            class="mb-2"
            color="red"
            dense
            max-width="100%"
            :dismissible="true"
            v-if="errorMessage"
          >{{ errorMessage }}</v-alert>
          <v-alert
            class="mb-2"
            color="success"
            dense
            max-width="100%"
            :dismissible="true"
            v-if="successMessage"
          >{{ successMessage }}</v-alert>

          <v-btn :disabled="!valid" color="deep-orange darken-3 white--text" class="mr-4" @click="validate()" :loading="loading">Ajouter</v-btn>
        </v-form>
      </v-col>
    </v-row>

    <v-row class="wrap d-flex justify-center align-content-center mt-4">
      <v-divider></v-divider>
    </v-row>

    <v-row class="wrap d-flex justify-center align-content-center">
      <v-col lg="8" sm="12" md="10">
        <h1 class="deep-orange--text darken-3">Vos projets en cours  ({{ myProjects.length }}) </h1>

        <v-row>
          <v-col v-for="(item, i) in myProjects" :key="i" cols="12" lg="6" sm="12" md="10">
            <v-card class="elevation-2">
              <v-card-title class="headline deep-orange darken-3 white--text" v-text="item.title" text-center></v-card-title>

              <v-card-text text-left class="mt-3">
                <h4>Type de besoin :</h4>
                {{ item.need }}
              </v-card-text>
              <v-card-text text-left>
                <h4>Présentation du projet d'aide :</h4>
                {{ item.presentation }}
              </v-card-text>
              <v-card-text text-left>
                <h4>Contact :</h4>
                {{ item.email }}
                {{ item.phone }}
              </v-card-text>
              <v-card-actions>
                <v-btn color="error" @click="dialogRemove = true, itemRemove = item.id">Supprimer</v-btn>
                <v-spacer></v-spacer>
                <v-switch :label='item.publied ? "Publié": "Dépublié"' color="deep-orange--text darken-3" v-model="item.publied" @change="updatePublied(item.id, item.publied)"></v-switch>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
        
        <v-dialog v-model="dialogRemove" max-width="400">
          <v-card>
            <v-card-title class="headline">Suppression définitive du projet</v-card-title>
            <v-card-text>Vous souhaitez vraiment supprimer ce projet ?</v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="error" :loading="loading" @click="remove(itemRemove)">Oui</v-btn>
              <v-btn color="success" @click="dialogRemove = false">Non</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-snackbar v-model="confirmProjectRemoved" :timeout="2000" light>
          Projet d'aide supprimé !
          <v-btn color="error" text @click="confirmProjectRemoved = false">Fermer</v-btn>
        </v-snackbar>

      </v-col>
    </v-row>
        <v-row class="wrap d-flex justify-center align-content-center mt-4">
      <v-divider></v-divider>
    </v-row>

    <v-row class="wrap d-flex justify-center align-content-center align-center">
      <v-col cols="12" class="d-flex wrap justify-center align-center align-content-center">
        <h3 cols="12" class="deep-orange--text darken-3">Supprimer définivement mon compte et tous mes projets</h3>
         <v-spacer></v-spacer>
        <v-btn color="error" @click="dialogRemoveAccount = true">Supprimer</v-btn>
       
        <v-dialog v-model="dialogRemoveAccount" max-width="300">
          <v-card>
            <v-card-title class="body-1">Suppression définitive de mon compte et de mes projets</v-card-title>
            <v-card-text>Vous souhaitez vraiment nous quitter :-/ ?</v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="error" @click="removeMyAccount()">Oui</v-btn>
              <v-btn color="success" @click="dialogRemoveAccount = false">Non</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

      </v-col>
    </v-row>

  </v-container>
</template>
<script>
import { mapGetters } from "vuex";
import needsJson from '../data/needs.json'
export default {
  name: "Account",
  data() {
    return {
      dialogRemove: false,
      dialogRemoveAccount: false,
      itemRemove: null,
      errorMessage: null,
      successMessage: null,
      confirmProjectRemoved: false,
      title: "",
      presentation: "",
      email: "",
      phone: "",
      need: "",
      publied: true,
      departement: "72",
      rgpd: false,
      valid: true,
      patternPhone: "",
      emailRules: [v => /.+@.+\..+/.test(v) || "L'email doit être valide"],
      phoneRules: [
        v => !v || true,
        v =>
          /^(0)[1-9](\d{2}){4}$/.test(v) ||
          "Le format du téléphone doit être : 0123456789"
      ],
      needs: needsJson
    };
  },
  mounted() {
    this.$store.dispatch("listenerMyProjects");
  },
  methods: {
    validate() {
      this.errorMessage = null;
      this.successMessage = null;
      if (this.email == "" && this.phone == "") {
        this.errorMessage =
          "Pour être contacter, il faut un email ou un téléphone de contact";
        return;
      } else {
        this.valid = true
      }
      if (this.valid == true) {
        this.save();
      }
    },
    save() {
      let d = new Date();
      let timestamp = d.getTime();
      let mail = "non communiqué";
      let tel = "non communiqué";
      let title = this.title.charAt(0).toUpperCase() + this.title.slice(1)
      if (this.email != "" || this.email != "undefined") {
        mail = this.email;
      }
      if (this.phone != "" || this.phone != "undefined") {
        tel = this.phone;
      }
      this.$store
        .dispatch("addMyProject", {
          title: title,
          presentation: this.presentation,
          email: mail,
          phone: tel,
          need: this.need,
          publied: this.publied,
          timestamp: timestamp,
          departement: this.departement,
          rgpd: this.rgpd
        })
        .then(() => {
          // if there is no error go to home page
          if (!this.$store.getters.error) {
            this.$refs.addNewProject.reset();
            this.title = this.presentation = this.email = this.phone = this.need =
              "";
            this.publied = true;
            this.rgpd = false;
            this.successMessage =
              "Votre projet d'aide a bien été enregistré, les bénévoles le veront dans l'accueil";
            this.errorMessage = null;
            setTimeout(() => {
              this.successMessage = null;
            }, 2000);
          }
        });
    },
    remove(id) {
      this.$store.dispatch("removeMyProject", { id: id }).then(() => {
        this.confirmProjectRemoved = true;
        this.itemRemove = null
        this.dialogRemove = false
      });
    },
    updatePublied(id, publied) {
      this.$store.dispatch('updatePubliedProject', {id: id, publied: publied})
    },
    removeMyAccount() {
      this.$store
      .dispatch('removeMyAccount')
      .then( () => this.$router.replace('/'))
    }
  },
  computed: {
    // mix the getters into computed with object spread operator
    ...mapGetters({
      myProjects: "myProjects",
      user: "user",
      loading: 'loading'
    })
  }
};
</script>
<style scoped>
</style>
